import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/helpers'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {motion} from 'framer-motion'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import type {TestimonialsSectionSliceV2} from 'prismicio-types'
import type {FC} from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {Gradient} from 'slices/SideGrid/components'
import type {TrackingProps} from 'src/common/helpers'
import {EVENTS, getLinkLocale, track} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {Icon} from 'src/common/Icon'
import type {Context} from 'src/common/types'

import {resolveTestimonialFromStory} from './resolveTestimonialFromStory'

const slideVariants = {
  visible: {opacity: 1, visibility: 'visible', transition: {delay: 0.4, duration: 0.7, ease: 'easeInOut'}},
  hidden: {opacity: 0, transition: {duration: 0.5}, transitionEnd: {visibility: 'hidden'}},
} as const

/**
 * Props for `TestimonialsSectionV2`.
 */
export type TestimonialsSectionV2Props = {
  slice: TestimonialsSectionSliceV2
  context: Context
  index: number
}

/**
 * Component for "TestimonialsSection" V2 variation.
 */
const TestimonialsSectionV2: FC<TestimonialsSectionV2Props> = ({slice, index: componentIndex, context}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [progress, setProgress] = useState(0)
  const {locale} = useRouter()
  const {ratings_section, button_text} = slice.primary
  const hasRatings = ratings_section.length > 0
  const legacyTag = 'tag' in slice.primary ? (slice.primary.tag as RichTextField) : undefined
  const eyebrow = isFilled.richText(slice.primary.eyebrow) ? slice.primary.eyebrow : legacyTag

  const testimonials = useMemo(
    () =>
      slice.primary.testimonials.map((testimonial) => resolveTestimonialFromStory(testimonial, button_text, locale)),
    [slice.primary.testimonials, button_text, locale],
  )

  const domainCountry = useGetDomainCountry()
  const {linkResolver} = useLinkResolver()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const startTimeRef = useRef<number | null>(null)

  const tabs = testimonials
    .filter((testimonial) => isFilled.keyText(testimonial.tab_title))
    .map((testimonial) => testimonial.tab_title as string)

  const hasHeaderSection = isFilled.richText(slice.primary.title) || tabs.length > 0
  const isTabNavigationEnabled = tabs.length > 0

  const mobileGridRowsWithRatings = isTabNavigationEnabled
    ? 'grid-rows-[minmax(400px,auto)_40px_168px_168px_168px_auto]'
    : 'grid-rows-[minmax(400px,auto)_72px_168px_168px_168px_auto]'

  const mobileGridRowsWithoutRatings = isTabNavigationEnabled
    ? 'grid-rows-[minmax(400px,auto)_40px_auto]'
    : 'grid-rows-[minmax(400px,auto)_72px_auto]'

  const mobileGridRows = hasRatings ? mobileGridRowsWithRatings : mobileGridRowsWithoutRatings
  const mobileBottomRowStart = hasRatings ? 'max-md:row-start-6' : 'max-md:row-start-3'
  const autoplayDuration = slice.primary.autoplay_duration ?? 0
  const isAutoplayEnabled = !isTabNavigationEnabled && autoplayDuration > 0 && testimonials.length > 1

  const handleClick = useCallback(
    (index: number, trackingData: TrackingProps, newTrackingData: Record<string, string>) => {
      track(trackingData)
      setActiveTab(index)

      trackMossAnalytics({
        event: 'interaction',
        interaction: {
          location: `component-${componentIndex}-testimonials-${index}`,
          type: 'click',
          element: 'category-tab',
          elementText: newTrackingData.elementText,
        },
        page: {
          domainCountry,
          type: context.pageUid || 'unknown',
        },
      })
    },
    [componentIndex, domainCountry, context.pageUid],
  )

  const handleNavigate = useCallback(
    (newIndex: number) => {
      setActiveTab(newIndex)
      setProgress(0)

      // Clear autoplay timer and animation when user manually navigates
      if (isAutoplayEnabled) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }

        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
          animationFrameRef.current = null
        }

        startTimeRef.current = null
      }
    },
    [isAutoplayEnabled],
  )

  // Progress animation effect
  useEffect(() => {
    if (!isAutoplayEnabled) {
      return
    }

    // Reset progress and start time
    setProgress(0)
    startTimeRef.current = null

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp
      }

      const elapsed = timestamp - startTimeRef.current
      const duration = autoplayDuration * 1000
      const newProgress = Math.min(elapsed / duration, 1)

      setProgress(newProgress)

      if (newProgress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate)
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [isAutoplayEnabled, autoplayDuration, activeTab])

  // Autoplay effect
  useEffect(() => {
    if (!isAutoplayEnabled) {
      return
    }

    // Start autoplay interval
    intervalRef.current = setInterval(() => {
      setActiveTab((current) => {
        const nextIndex = current + 1
        // Loop back to start when reaching the end
        return nextIndex >= testimonials.length ? 0 : nextIndex
      })
    }, autoplayDuration * 1000)

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isAutoplayEnabled, autoplayDuration, testimonials.length])

  // Restart autoplay after manual navigation
  useEffect(() => {
    if (isAutoplayEnabled && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setActiveTab((current) => {
          const nextIndex = current + 1
          return nextIndex >= testimonials.length ? 0 : nextIndex
        })
      }, autoplayDuration * 1000)
    }
  }, [activeTab, isAutoplayEnabled, autoplayDuration, testimonials.length])

  if (testimonials.length === 0) {
    return null
  }

  return (
    <div
      className={cn('flex flex-col bg-moss-white pb-0 max-lg:pt-10', {
        'lg:pt-20': hasHeaderSection,
      })}
    >
      {hasHeaderSection ? (
        <div
          className={cn('mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-5 md:gap-12 md:px-10 lg:px-5 xl:px-0', {
            'max-md:pb-6': !isTabNavigationEnabled,
          })}
        >
          <div className="flex flex-col gap-3 md:gap-6">
            <PrismicRichText
              field={eyebrow}
              components={{
                paragraph: ({children}) => (
                  <p className="text-tg-body-s md:text-button-s font-semibold uppercase text-moss-grey-400">
                    {children}
                  </p>
                ),
                strong: ({children}) => <strong className="pr-3 text-moss-blue">{children}</strong>,
              }}
            />
            <PrismicRichText
              components={{
                heading1: ({children}) => (
                  <h2 className="md:text-tg-h2 text-[32px] font-normal leading-9 tracking-[-.32px] text-black md:leading-[normal]">
                    {children}
                  </h2>
                ),
                strong: ({children}) => (
                  <>
                    <span className="md:text-tg-h2 text-[32px] leading-9 tracking-[-.32px] text-black md:leading-[normal]">
                      {children}
                    </span>
                    <br className="max-lg:hidden" />
                  </>
                ),
              }}
              field={slice.primary.title}
            />
          </div>
          {tabs.length > 0 && (
            <div className="flex gap-2 max-md:pb-10">
              {tabs.map((tab, index) => (
                <button
                  key={index}
                  onClick={() =>
                    handleClick(
                      index,
                      {
                        event: EVENTS.CLICK,
                        location: `component-${componentIndex}-testimonials`,
                        pageName: context.pageUid,
                        mktgId: `testimonial-tab-${index}`,
                        dataType: 'testimonial-tab',
                      },
                      {
                        elementText: tab!,
                      },
                    )
                  }
                  className={cn(
                    'md:text-button-s h-8 rounded-[4px] px-3 py-[9px] text-[11px] font-semibold uppercase tracking-[.66px]',
                    {
                      'bg-green-neon text-black': index === activeTab,
                      'bg-moss-grey-100 text-moss-grey-500 hover:bg-moss-grey-150': index !== activeTab,
                    },
                  )}
                >
                  {tab}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : null}
      <div
        className={cn(
          'relative isolate grid w-full grid-cols-[minmax(20px,_1fr)_repeat(12,_minmax(auto,_100px))_minmax(20px,_1fr)] md:max-lg:grid-cols-[minmax(40px,_1fr)_repeat(12,_minmax(auto,_100px))_minmax(40px,_1fr)]',
          hasRatings
            ? 'md:grid-rows-[64px,minmax(500px,auto),auto,168px,80px] lg:grid-rows-[64px,556px,auto,168px,80px]'
            : 'md:grid-rows-[64px,minmax(500px,auto),auto,80px] lg:grid-rows-[64px,556px,auto,80px]',
          mobileGridRows,
        )}
      >
        <Gradient color="255, 255, 255" gradientStart={20} height={40} location="top" className="max-md:hidden" />

        {/* First row */}
        <div className="col-span-1 rounded-br-[4px] border-b border-r border-moss-grey-150 max-md:row-start-2" />
        <div
          id="track"
          className={cn(
            'col-span-12 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:relative',
            {
              'max-md:row-start-2': isTabNavigationEnabled,
              'max-md:col-start-2 max-md:border-b-0 max-md:border-t max-md:rounded-br-0 max-md:mt-[-1px] max-md:rounded-t-[4px]':
                !isTabNavigationEnabled,
            },
            !isTabNavigationEnabled && mobileBottomRowStart,
          )}
        >
          <Gradient
            color="255, 255, 255"
            gradientStart={20}
            height={40}
            location={isTabNavigationEnabled ? 'top' : 'bottom'}
            className="left-[-1px] w-[calc(100%+10px)] md:hidden"
          />
        </div>
        <div className="col-span-1 ml-[-1px] rounded-bl-[4px] border-b border-l border-moss-grey-150 max-md:row-start-2" />

        {/* Image section */}
        <div
          className={cn(
            'col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 max-md:col-start-1 max-md:row-start-3',
            {'max-md:hidden': !hasRatings},
          )}
        />
        <div className="col-span-12 ml-[-1px] mt-[-1px] overflow-hidden rounded-[4px] border border-moss-grey-150 bg-green-dark max-md:relative max-md:col-start-2 max-md:border-none md:col-span-12">
          <div className="grid h-full">
            {testimonials.map((testimonial, index) => (
              <motion.div
                className="grid size-full min-w-full grid-cols-[138px_auto_auto] grid-rows-[1fr_auto_auto] gap-x-4 gap-y-6 rounded-lg bg-green-dark p-6 [grid-area:1/1] md:max-lg:grid-rows-[auto_auto] lg:max-w-[1176px] lg:grid-cols-2 lg:grid-rows-[auto_1fr_auto] lg:p-10 xl:gap-x-16"
                key={index}
                variants={slideVariants}
                initial={false}
                animate={index === activeTab ? 'visible' : 'hidden'}
                aria-hidden={index !== activeTab}
              >
                {/* Left content section */}
                <div className="col-span-full md:max-lg:max-w-[500px] lg:col-span-1">
                  {/* <div className="flex flex-col gap-6"> */}
                  <div className="flex flex-col gap-4 lg:gap-6">
                    <div className="flex size-8 items-center justify-center rounded-[4px] border border-[rgba(0,0,0,0.04)] bg-green-neon p-[7px]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="17"
                        height="9"
                        viewBox="0 0 21 11"
                        fill="none"
                        className="text-green-dark"
                      >
                        <path
                          d="M9.406 0.000122404L6.94034 0.000122296C3.06474 0.000122127 0.5 2.67885 0.5 6.55445L0.5 11H4.83156L4.83156 4.84463H9.406L9.406 0.000122404Z"
                          fill="currentColor"
                        />
                        <path
                          d="M20.8483 1.07773e-07L18.3826 0C14.507 -1.69408e-07 11.9423 2.67873 11.9423 6.55433V10.9999H16.2738V4.84451H20.8483V1.07773e-07Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <PrismicRichText
                      field={testimonial.quote}
                      components={{
                        paragraph: ({children}) => (
                          <p className="text-[22px] font-normal leading-[24px] tracking-[-0.16px] text-moss-white md:text-[32px] md:leading-[38px]">
                            {children}
                          </p>
                        ),
                      }}
                    />
                  </div>
                </div>

                {/* Right image section */}
                <div className="relative isolate col-span-1 flex aspect-[6/5] size-[138px] max-h-[138px] max-w-[138px] flex-col items-end justify-end overflow-hidden rounded-[4px] bg-moss-white p-6 max-lg:col-start-1 max-lg:self-end max-lg:object-cover max-md:row-start-2 max-md:mt-4 lg:col-start-2 lg:row-span-3 lg:aspect-auto lg:size-full lg:max-h-none lg:max-w-none">
                  {isFilled.image(testimonial.image) && (
                    <PrismicNextImage
                      field={testimonial.image}
                      fill
                      sizes="(min-width: 1024px) 560px, 138px"
                      quality={90}
                      priority={index === 0}
                      className="overflow-hidden object-cover"
                    />
                  )}
                  <div
                    className="absolute inset-0 top-auto h-1/3 blur-[10px] md:bottom-[-10px]"
                    style={{
                      background: 'linear-gradient(to bottom, transparent 20%, rgba(22, 21, 21, 0.7) 100%)',
                    }}
                  />
                  {isFilled.image(testimonial.company_logo) && (
                    <div className="relative z-10 flex h-20 w-56 items-center justify-center rounded-[4px] border border-moss-white/[30%] bg-moss-white/[20%] backdrop-blur-[10px] max-lg:hidden">
                      <div className="relative h-9 w-40">
                        <PrismicNextImage
                          field={testimonial.company_logo}
                          fill
                          sizes="160px"
                          className="object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-start-2 row-start-2 flex gap-4 max-lg:flex max-lg:justify-between max-lg:self-end max-md:col-span-2 max-md:mt-4 max-md:h-[138px] max-md:self-end lg:col-start-1 lg:row-start-2 lg:mt-auto">
                  <div className="flex flex-col max-md:justify-end max-md:gap-4">
                    <div className="flex flex-col gap-1 lg:gap-2">
                      <p className="text-button uppercase text-moss-white">{testimonial.author_name}</p>
                      <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-3">
                        {isFilled.keyText(testimonial.author_role) && (
                          <p className="text-button text-moss-white/70">{testimonial.author_role}</p>
                        )}
                        {isFilled.keyText(testimonial.author_role) && isFilled.keyText(testimonial.author_company) && (
                          <div className="hidden size-1 rounded bg-moss-white/70 md:block" />
                        )}
                        {isFilled.keyText(testimonial.author_company) && (
                          <p className="text-button text-moss-white/70">{testimonial.author_company}</p>
                        )}
                      </div>
                    </div>
                    {isFilled.image(testimonial.company_logo) && (
                      <PrismicNextImage
                        field={testimonial.company_logo}
                        className="max-h-6 w-auto max-w-[140px] object-contain object-left md:hidden"
                      />
                    )}
                  </div>
                </div>
                {isFilled.keyText(testimonial.button_label) && isFilled.link(testimonial.link_url) && (
                  <Link
                    href={linkResolver(testimonial.link_url)}
                    locale={getLinkLocale(testimonial.link_url, locale)}
                    className="group relative z-10 flex h-[38px] w-fit items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100 max-lg:self-end max-md:col-span-full max-md:col-start-1 max-md:row-start-3 max-md:mt-4 max-md:w-full max-md:justify-between md:max-lg:justify-self-end lg:col-start-1 lg:row-start-3 lg:self-end"
                    onClick={() => {
                      track({
                        event: EVENTS.CLICK,
                        mktgId: `testimonial-${index}-button`,
                        location: `component-${componentIndex}-testimonials`,
                        pageName: context.pageUid,
                        dataType: 'customer-story-button',
                      })

                      trackMossAnalytics({
                        event: 'interaction',
                        interaction: {
                          element: `testimonial-lean-more-button`,
                          location: `component-${componentIndex}-testimonials-${index}`,
                          type: 'click',
                        },
                        page: {
                          domainCountry,
                          type: context.pageUid || 'unknown',
                        },
                      })
                    }}
                  >
                    <span className="text-button-s uppercase text-black">{testimonial.button_label}</span>
                    <div className="flex size-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
                      <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
                    </div>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
        <div
          className={cn(
            'col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 max-md:col-start-[14] max-md:row-start-4',
            {'max-md:hidden': !hasRatings},
          )}
        />

        {/* Divider section */}
        <div
          className={cn(
            'col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 max-md:col-start-1 max-md:row-start-4',
            {
              'h-10': isTabNavigationEnabled,
              'max-md:hidden': !hasRatings,
            },
          )}
        />
        <div
          className={cn(
            'col-span-12 ml-[-1px] mt-[-1px] flex items-center justify-center rounded-[4px] border border-moss-grey-150 max-md:col-start-2',
            {
              'h-[72px] md:h-[120px] max-md:row-start-2 max-md:rounded-t-none max-md:rounded-b-[4px] max-md:border-t-0 max-md:mt-0':
                !isTabNavigationEnabled,
              'h-10': isTabNavigationEnabled,
            },
            isTabNavigationEnabled && mobileBottomRowStart,
          )}
        >
          {tabs.length === 0 && (
            <div className="flex items-center gap-12 max-md:gap-6">
              <button
                onClick={() => handleNavigate(Math.max(0, activeTab - 1))}
                className="flex items-center justify-center"
                aria-label="Previous testimonial"
              >
                <Icon name="arrow" className="h-3 w-[13px] scale-x-flip text-moss-grey-500" />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <div
                    key={`nav-dot-${index}`}
                    className={cn('relative h-2 overflow-hidden rounded-[1px] transition-all duration-300', {
                      'w-2 bg-moss-grey-150': index !== activeTab,
                      'w-8 bg-moss-grey-150': index === activeTab && isAutoplayEnabled,
                      'w-8 bg-green-neon': index === activeTab && !isAutoplayEnabled,
                    })}
                  >
                    {index === activeTab && isAutoplayEnabled && (
                      <div
                        className="absolute size-full origin-left bg-green-neon"
                        style={{transform: `scaleX(${progress})`}}
                      />
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleNavigate(Math.min(testimonials.length - 1, activeTab + 1))}
                className="flex items-center justify-center"
                aria-label="Next testimonial"
              >
                <Icon name="arrow" className="h-3 w-[13px] text-moss-grey-500" />
              </button>
            </div>
          )}
        </div>
        <div
          className={cn(
            'col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 max-md:col-start-[14] max-md:row-start-3',
            {
              'h-10': isTabNavigationEnabled,
              'max-md:hidden': !hasRatings,
            },
          )}
        />

        {/* Ratings section */}
        {hasRatings && (
          <>
            <div className="col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 max-md:row-start-5" />
            {ratings_section.map((rating, index) => (
              <div
                key={'ratings-' + index}
                // eslint-disable-next-line tailwindcss/no-custom-classname -- arbitrary value using the index
                className="max-md:row-start- col-span-4 ml-[-1px] mt-[-1px] flex flex-col items-center justify-center gap-2 rounded-[4px] border border-moss-grey-150 p-4 max-md:col-span-12 max-md:col-start-2"
              >
                <PrismicRichText
                  field={rating.content}
                  components={{
                    image: ({node}) => <img src={node.url} alt=" " className="size-8" />,
                    heading5: ({children}) => (
                      <h5 className="text-tg-h3 text-center font-normal text-[#282828]">{children}</h5>
                    ),
                    em: ({children}) => (
                      <>
                        <span>{children}</span>
                        <br />
                      </>
                    ),
                    paragraph: ({children}) => (
                      <p className="text-detail text-center font-semibold uppercase leading-[normal] text-[#282828]/[60%]">
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>
            ))}
            <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150" />
          </>
        )}

        {/* Last row */}
        <div
          className={cn(
            'col-span-1 mt-[-1px] rounded-tr-[4px] border-r border-t border-moss-grey-150',
            mobileBottomRowStart,
          )}
        />
        <div className="col-span-4 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150" />
        <div className="col-span-4 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150" />
        <div className="col-span-4 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150" />
        <div
          className={cn(
            'col-span-1 ml-[-1px] mt-[-1px] rounded-tl-[4px] border-l border-t border-moss-grey-150',
            mobileBottomRowStart,
          )}
        />

        <Gradient color="255, 255, 255" gradientStart={20} height={40} location="bottom" />
      </div>
    </div>
  )
}

export default TestimonialsSectionV2
